import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate, useLocation, Outlet } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { 
  LayoutDashboard, FileText, AlertCircle, Users, DollarSign, 
  LogOut, Menu, X, ChevronRight, TrendingUp, Clock, CheckCircle2,
  BarChart3, PieChart, Activity, UserCog, FileSearch, Settings, Banknote, FileStack, Eye, FileSignature,
  Headphones, BookOpen, Gift, Bell, Wallet, Briefcase, Download, Search, Star, ChevronDown, User,
  MessageSquare, Network, Mail, Tag, CreditCard, Presentation, Printer
} from "lucide-react";
import { NestIcon } from "../../components/NestIcon";
import { NotificationBell } from "../../components/NotificationBell";
import { ChannelsBell } from "../../components/ChannelsBell";
import { OnboardingTour, TourHelpButton } from "../../components/OnboardingTour";
import { PreferencesDialog } from "../../components/Preferences";
import { API } from "../../App";
import axios from "axios";
import { toast } from "sonner";

// Role-based permissions for menu visibility
const MENU_PERMISSIONS = {
  dashboard: ["all"],
  analytics: ["super_admin", "admin", "manager", "hr"],
  quotes: ["super_admin", "admin", "manager", "power_user", "agent"],
  claims: ["super_admin", "admin", "manager", "power_user", "claims_processor", "agent"],
  customers: ["super_admin", "admin", "manager", "power_user", "agent"],
  contracts: ["super_admin", "admin", "manager", "power_user"],
  esignatures: ["super_admin", "admin", "manager", "power_user"],
  support: ["super_admin", "admin", "manager", "power_user", "agent", "hr"],
  knowledgebase: ["super_admin", "admin", "manager", "power_user", "hr"],
  referrals: ["super_admin", "admin", "manager"],
  careers: ["super_admin", "admin", "manager", "hr"],
  payments: ["super_admin", "admin", "manager"],
  withdrawals: ["super_admin", "admin", "manager"],
  commissions: ["all"],
  team: ["super_admin", "admin", "manager", "hr"],
  teamstatus: ["super_admin", "admin", "manager", "hr"],
  hr: ["super_admin", "admin", "manager", "hr"],
  selfservice: ["all"],
  orgchart: ["all"],
  channels: ["all"],
  audit: ["super_admin", "admin"],
  settings: ["super_admin", "admin"],
  systemhealth: ["super_admin", "admin"],
  newsletters: ["super_admin", "admin"],
  promocodes: ["super_admin", "admin"],
  stripedashboard: ["super_admin", "admin"],
  marketing: ["super_admin", "admin", "manager"],
  visitorinsights: ["super_admin", "admin"]
};

const canAccessMenu = (role, menuKey) => {
  if (!role) return false;
  const allowedRoles = MENU_PERMISSIONS[menuKey] || [];
  return allowedRoles.includes("all") || allowedRoles.includes(role);
};

// Menu items organized by category
const MENU_GROUPS = {
  main: {
    label: "Main",
    tourId: "main-section",
    items: [
      { path: "/agent/dashboard", icon: LayoutDashboard, label: "Dashboard", key: "dashboard", tourId: "agent-dashboard" },
      { path: "/agent/analytics", icon: BarChart3, label: "Analytics", key: "analytics", tourId: "analytics" },
    ]
  },
  crm: {
    label: "CRM",
    tourId: "crm-section",
    items: [
      { path: "/agent/quotes", icon: FileText, label: "Quotes", key: "quotes", tourId: "quotes" },
      { path: "/agent/claims", icon: AlertCircle, label: "Claims", key: "claims", tourId: "claims" },
      { path: "/agent/customers", icon: Users, label: "Customers", key: "customers", tourId: "customers" },
      { path: "/agent/contracts", icon: FileStack, label: "Policy Contracts", key: "contracts", tourId: "contracts" },
      { path: "/agent/e-signatures", icon: FileSignature, label: "E-Signatures", key: "esignatures", tourId: "e-signatures" },
    ]
  },
  content: {
    label: "Content & Support",
    tourId: "content-section",
    items: [
      { path: "/agent/support-tickets", icon: Headphones, label: "Support Tickets", key: "support", tourId: "support-tickets" },
      { path: "/agent/knowledge-base", icon: BookOpen, label: "Knowledge Base", key: "knowledgebase", tourId: "knowledge-base" },
      { path: "/agent/careers", icon: Briefcase, label: "Careers", key: "careers", tourId: "careers" },
    ]
  },
  finance: {
    label: "Finance",
    tourId: "finance-section",
    items: [
      { path: "/agent/payments", icon: DollarSign, label: "Payments", key: "payments", tourId: "payments" },
      { path: "/agent/withdrawals", icon: Banknote, label: "Withdrawals", key: "withdrawals", tourId: "withdrawals" },
      { path: "/agent/my-commissions", icon: Wallet, label: "My Commissions", key: "commissions", tourId: "commissions" },
      { path: "/agent/referrals", icon: Gift, label: "Referrals", key: "referrals", tourId: "referrals" },
      { path: "/agent/stripe-dashboard", icon: CreditCard, label: "Stripe Dashboard", key: "stripedashboard", tourId: "stripe-dashboard" },
    ]
  },
  marketing: {
    label: "Marketing",
    tourId: "marketing-section",
    items: [
      { path: "/agent/marketing", icon: Presentation, label: "Marketing Assets", key: "marketing", tourId: "marketing-assets" },
      { path: "/agent/flyer-generator", icon: Printer, label: "Flyer & Brochure", key: "flyergenerator", tourId: "flyer-generator" },
      { path: "/agent/newsletters", icon: Mail, label: "Newsletters", key: "newsletters", tourId: "newsletters" },
      { path: "/agent/promo-codes", icon: Tag, label: "Promo Codes", key: "promocodes", tourId: "promo-codes" },
      { path: "/agent/visitor-insights", icon: Activity, label: "Visitor Insights", key: "visitorinsights", tourId: "visitor-insights" },
    ]
  },
  team: {
    label: "Team",
    tourId: "team-section",
    items: [
      { path: "/agent/team", icon: UserCog, label: "Team Members", key: "team", tourId: "team-members" },
      { path: "/agent/team-status", icon: Activity, label: "Team Status", key: "teamstatus", tourId: "team-status" },
      { path: "/agent/hr", icon: Briefcase, label: "HR Management", key: "hr", tourId: "hr-management" },
      { path: "/agent/self-service", icon: User, label: "My HR Profile", key: "selfservice", tourId: "self-service" },
      { path: "/agent/org-chart", icon: Network, label: "Org Chart", key: "orgchart", tourId: "org-chart" },
      { path: "/agent/channels", icon: MessageSquare, label: "Channels", key: "channels", tourId: "channels" },
    ]
  },
  admin: {
    label: "Administration",
    tourId: "admin-section",
    items: [
      { path: "/agent/audit-logs", icon: FileSearch, label: "Audit Logs", key: "audit", tourId: "audit-logs" },
      { path: "/agent/system-health", icon: Activity, label: "System Health", key: "systemhealth", tourId: "system-health" },
      { path: "/agent/settings", icon: Settings, label: "Settings", key: "settings", tourId: "settings" },
    ]
  }
};

// Shared Agent Layout Component
export function AgentLayout({ children, title }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [agent, setAgent] = useState(() => {
    // Try to get cached agent data
    const cached = sessionStorage.getItem("agent_data");
    return cached ? JSON.parse(cached) : null;
  });
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [favorites, setFavorites] = useState([]);
  const [expandedGroups, setExpandedGroups] = useState({
    main: true, crm: true, content: true, finance: true, marketing: true, team: true, admin: true
  });

  // Load favorites from localStorage
  useEffect(() => {
    const savedFavorites = localStorage.getItem("agent_menu_favorites");
    if (savedFavorites) {
      setFavorites(JSON.parse(savedFavorites));
    }
  }, []);

  // Fetch agent data only if not cached
  useEffect(() => {
    const token = localStorage.getItem("agent_token");
    if (token && !agent) {
      axios.get(`${API}/agent/me`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      .then(res => {
        setAgent(res.data);
        sessionStorage.setItem("agent_data", JSON.stringify(res.data));
      })
      .catch(() => {});
    }
  }, [agent]);

  const handleLogout = () => {
    localStorage.removeItem("agent_token");
    sessionStorage.removeItem("agent_data");
    navigate("/agent/login");
  };

  const toggleFavorite = useCallback((path) => {
    setFavorites(prev => {
      const newFavorites = prev.includes(path) 
        ? prev.filter(p => p !== path)
        : [...prev, path];
      localStorage.setItem("agent_menu_favorites", JSON.stringify(newFavorites));
      return newFavorites;
    });
  }, []);

  const toggleGroup = (groupKey) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupKey]: !prev[groupKey]
    }));
  };

  // Get all menu items filtered by role
  const getAllMenuItems = () => {
    const items = [];
    Object.values(MENU_GROUPS).forEach(group => {
      group.items.forEach(item => {
        if (canAccessMenu(agent?.role, item.key)) {
          items.push(item);
        }
      });
    });
    return items;
  };

  // Filter items based on search
  const filteredItems = searchQuery 
    ? getAllMenuItems().filter(item => 
        item.label.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : null;

  // Get favorite items
  const favoriteItems = getAllMenuItems().filter(item => favorites.includes(item.path));

  const renderMenuItem = (item, showStar = true) => {
    const isActive = location.pathname === item.path;
    
    const handleNavigation = (e) => {
      e.preventDefault();
      setSidebarOpen(false);
      // Use navigate for smoother transitions without reload
      if (location.pathname !== item.path) {
        navigate(item.path);
      }
    };
    
    return (
      <div key={item.path} className="relative group" data-tour={item.tourId}>
        <a
          href={item.path}
          className={`sidebar-link ${isActive ? 'active' : ''}`}
          onClick={handleNavigation}
        >
          <item.icon className="h-5 w-5" />
          <span className="flex-1">{item.label}</span>
          {showStar && (
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                toggleFavorite(item.path);
              }}
              className="opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-primary/10 rounded"
            >
              <Star 
                className={`h-4 w-4 ${favorites.includes(item.path) ? 'fill-yellow-400 text-yellow-400' : 'text-muted-foreground'}`} 
              />
            </button>
          )}
        </a>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Sidebar */}
      <aside className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="px-6 py-4 border-b border-border">
          <a 
            href="/agent/dashboard" 
            className="flex items-center gap-2"
            onClick={(e) => {
              e.preventDefault();
              navigate("/agent/dashboard");
            }}
          >
            <NestIcon className="h-7 w-7 text-primary" />
            <span className="text-lg font-bold text-primary" style={{ fontFamily: 'Young Serif, serif' }}>
              TheRentPayer
            </span>
          </a>
          <p className="text-xs mt-1" style={{ color: 'hsl(var(--sidebar-text))' }}>Agent Portal</p>
        </div>

        {/* Global Search */}
        <div className="px-4 py-3 border-b border-border">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4" style={{ color: 'hsl(var(--sidebar-text))' }} />
            <Input
              placeholder="Search menu..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 h-9"
              style={{ background: 'hsl(var(--sidebar-hover))', color: 'hsl(var(--sidebar-text-active))' }}
              data-testid="sidebar-search"
            />
          </div>
        </div>

        <nav className="py-2 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 280px)' }}>
          {/* Search Results */}
          {filteredItems && (
            <div className="px-4 py-2">
              <p className="text-xs font-medium mb-2" style={{ color: 'hsl(var(--sidebar-text))' }}>
                Search Results ({filteredItems.length})
              </p>
              {filteredItems.length === 0 ? (
                <p className="text-sm px-2" style={{ color: 'hsl(var(--sidebar-text))' }}>No results found</p>
              ) : (
                filteredItems.map(item => renderMenuItem(item))
              )}
            </div>
          )}

          {/* Regular Menu (when not searching) */}
          {!filteredItems && (
            <>
              {/* Favorites Section */}
              {favoriteItems.length > 0 && (
                <div className="mb-2">
                  <div className="px-4 py-2">
                    <p className="text-xs font-semibold text-primary flex items-center gap-1">
                      <Star className="h-3 w-3 fill-yellow-400 text-yellow-400" />
                      Favorites
                    </p>
                  </div>
                  {favoriteItems.map(item => renderMenuItem(item, false))}
                  <div className="border-b border-border mx-4 my-2" />
                </div>
              )}

              {/* Grouped Menu Items */}
              {Object.entries(MENU_GROUPS).map(([groupKey, group]) => {
                const visibleItems = group.items.filter(item => canAccessMenu(agent?.role, item.key));
                if (visibleItems.length === 0) return null;

                return (
                  <div key={groupKey} className="mb-1" data-tour={group.tourId}>
                    <button
                      onClick={() => toggleGroup(groupKey)}
                      className="w-full px-4 py-2 flex items-center justify-between text-xs font-semibold transition-colors"
                      style={{ color: 'hsl(var(--sidebar-text))' }}
                    >
                      <span>{group.label}</span>
                      <ChevronDown className={`h-4 w-4 transition-transform ${expandedGroups[groupKey] ? '' : '-rotate-90'}`} />
                    </button>
                    {expandedGroups[groupKey] && (
                      <div className="space-y-0.5">
                        {visibleItems.map(item => renderMenuItem(item))}
                      </div>
                    )}
                  </div>
                );
              })}
            </>
          )}
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-border" style={{ background: 'hsl(var(--sidebar-bg))' }}>
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
              <span className="text-primary font-semibold">
                {agent?.full_name?.charAt(0) || 'A'}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate" style={{ color: 'hsl(var(--sidebar-text-active))' }}>{agent?.full_name}</p>
              <p className="text-xs" style={{ color: 'hsl(var(--sidebar-text))' }}>{agent?.role}</p>
            </div>
          </div>
          <Button variant="outline" size="sm" className="w-full" onClick={handleLogout}>
            <LogOut className="h-4 w-4 mr-2" />
            Sign Out
          </Button>
        </div>
      </aside>

      {/* Onboarding Tour */}
      <OnboardingTour userType="agent" userRole={agent?.role} />

      {/* Mobile Header */}
      <div className="lg:hidden glass-header sticky top-0 z-40">
        <div className="flex items-center justify-between px-4 h-14">
          <button onClick={() => setSidebarOpen(!sidebarOpen)} className="p-2">
            {sidebarOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
          <span className="font-semibold">{title}</span>
          <div className="flex items-center gap-2">
            <TourHelpButton userType="agent" userRole={agent?.role} />
            <ChannelsBell />
            <NotificationBell token={localStorage.getItem("agent_token")} isAgent={true} />
          </div>
        </div>
      </div>

      {/* Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      {/* Desktop Header */}
      <header className="hidden lg:flex sticky top-0 z-30 bg-background/95 backdrop-blur border-b border-border h-14 items-center justify-end px-6 gap-4" data-tour="notifications">
        <PreferencesDialog />
        <TourHelpButton userType="agent" userRole={agent?.role} />
        <ChannelsBell />
        <NotificationBell token={localStorage.getItem("agent_token")} isAgent={true} />
      </header>

      <main className="main-content-with-sidebar p-6 lg:p-8">
        {children}
      </main>
    </div>
  );
}

export default function AgentDashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [recentClaims, setRecentClaims] = useState([]);
  const [recentQuotes, setRecentQuotes] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    const token = localStorage.getItem("agent_token");
    try {
      const [statsRes, claimsRes, quotesRes, analyticsRes, activityRes] = await Promise.all([
        axios.get(`${API}/agent/dashboard/stats`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/agent/claims?status=pending`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/agent/quotes?status=pending`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/agent/analytics/overview`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: null })),
        axios.get(`${API}/agent/analytics/recent-activity?limit=5`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] }))
      ]);

      setStats(statsRes.data);
      setRecentClaims(claimsRes.data.slice(0, 5));
      setRecentQuotes(quotesRes.data.slice(0, 5));
      setAnalytics(analyticsRes.data);
      setRecentActivity(activityRes.data || []);
    } catch (error) {
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: "bg-yellow-500/15 text-yellow-600 dark:bg-yellow-500/20 dark:text-yellow-400",
      approved: "bg-green-500/15 text-green-600 dark:bg-green-500/20 dark:text-green-400",
      rejected: "bg-red-500/15 text-red-600 dark:bg-red-500/20 dark:text-red-400",
      active: "bg-blue-500/15 text-blue-600 dark:bg-blue-500/20 dark:text-blue-400",
      under_review: "bg-purple-500/15 text-purple-600 dark:bg-purple-500/20 dark:text-purple-400"
    };
    return styles[status] || "bg-muted text-muted-foreground";
  };

  if (loading) {
    return (
      <AgentLayout title="Dashboard">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </AgentLayout>
    );
  }

  return (
    <AgentLayout title="Dashboard">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-foreground" style={{ fontFamily: 'Young Serif, serif' }}>
          Dashboard Overview
        </h1>
        <p className="text-muted-foreground">Welcome to TheRentPayer Agent Portal</p>
      </div>

      {/* Stats Grid - Clickable Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-testid="agent-dashboard">
        <Card 
          className="dashboard-card cursor-pointer hover:border-primary hover:shadow-md transition-all"
          onClick={() => navigate("/agent/customers")}
          data-testid="stat-card-users"
        >
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold text-foreground">{stats?.users?.total || 0}</p>
                <p className="text-sm text-muted-foreground">Total Users</p>
              </div>
              <div className="flex items-center gap-2">
                <div className="bg-blue-100 p-3 rounded-lg">
                  <Users className="h-6 w-6 text-blue-600" />
                </div>
                <Eye className="h-4 w-4 text-muted-foreground" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card 
          className="dashboard-card cursor-pointer hover:border-primary hover:shadow-md transition-all"
          onClick={() => navigate("/agent/contracts")}
          data-testid="stat-card-policies"
        >
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold text-foreground">{stats?.policies?.active || 0}</p>
                <p className="text-sm text-muted-foreground">Active Policies</p>
              </div>
              <div className="flex items-center gap-2">
                <div className="bg-green-100 p-3 rounded-lg">
                  <NestIcon className="h-6 w-6 text-green-600" />
                </div>
                <Eye className="h-4 w-4 text-muted-foreground" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card 
          className="dashboard-card cursor-pointer hover:border-primary hover:shadow-md transition-all"
          onClick={() => navigate("/agent/quotes")}
          data-testid="stat-card-quotes"
        >
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold text-foreground">{stats?.quotes?.pending || 0}</p>
                <p className="text-sm text-muted-foreground">Pending Quotes</p>
              </div>
              <div className="flex items-center gap-2">
                <div className="bg-amber-100 p-3 rounded-lg">
                  <FileText className="h-6 w-6 text-amber-600" />
                </div>
                <Eye className="h-4 w-4 text-muted-foreground" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card 
          className="dashboard-card cursor-pointer hover:border-primary hover:shadow-md transition-all"
          onClick={() => navigate("/agent/claims")}
          data-testid="stat-card-claims"
        >
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold text-foreground">{stats?.claims?.pending || 0}</p>
                <p className="text-sm text-muted-foreground">Pending Claims</p>
              </div>
              <div className="flex items-center gap-2">
                <div className="bg-red-100 p-3 rounded-lg">
                  <AlertCircle className="h-6 w-6 text-red-600" />
                </div>
                <Eye className="h-4 w-4 text-muted-foreground" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Analytics Summary */}
      {analytics && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200">
            <CardContent className="pt-6">
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp className="h-5 w-5 text-emerald-600" />
                <span className="text-sm font-medium text-emerald-700">Total Revenue</span>
              </div>
              <p className="text-2xl font-bold text-emerald-800">
                ${analytics.revenue?.total_revenue?.toLocaleString() || 0}
              </p>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
            <CardContent className="pt-6">
              <div className="flex items-center gap-2 mb-2">
                <DollarSign className="h-5 w-5 text-blue-600" />
                <span className="text-sm font-medium text-blue-700">Avg Premium</span>
              </div>
              <p className="text-2xl font-bold text-blue-800">
                ${analytics.revenue?.average_premium?.toFixed(2) || 0}
              </p>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
            <CardContent className="pt-6">
              <div className="flex items-center gap-2 mb-2">
                <Activity className="h-5 w-5 text-purple-600" />
                <span className="text-sm font-medium text-purple-700">New Users (30d)</span>
              </div>
              <p className="text-2xl font-bold text-purple-800">
                {analytics.users?.new_this_month || 0}
              </p>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
            <CardContent className="pt-6">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle2 className="h-5 w-5 text-amber-600" />
                <span className="text-sm font-medium text-amber-700">Conversion Rate</span>
              </div>
              <p className="text-2xl font-bold text-amber-800">
                {analytics.quotes?.conversion_rate?.toFixed(1) || 0}%
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Pending Claims */}
        <Card className="card-organic">
          <CardHeader>
            <div className="flex justify-between items-center">
              <CardTitle style={{ fontFamily: 'Young Serif, serif' }}>Pending Claims</CardTitle>
              <Link to="/agent/claims">
                <Button variant="ghost" size="sm">
                  View All <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </Link>
            </div>
          </CardHeader>
          <CardContent>
            {recentClaims.length > 0 ? (
              <div className="space-y-3">
                {recentClaims.map((claim) => (
                  <div 
                    key={claim.claim_id} 
                    className="flex items-center justify-between p-3 bg-muted/50 rounded-lg cursor-pointer hover:bg-muted"
                    onClick={() => navigate(`/agent/claims/${claim.claim_id}`)}
                  >
                    <div>
                      <p className="font-medium text-sm">{claim.user?.full_name || claim.user?.email}</p>
                      <p className="text-xs text-muted-foreground font-mono">{claim.claim_id}</p>
                    </div>
                    <Badge className={getStatusBadge(claim.status)}>{claim.status}</Badge>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-muted-foreground py-8">No pending claims</p>
            )}
          </CardContent>
        </Card>

        {/* Pending Quotes */}
        <Card className="card-organic">
          <CardHeader>
            <div className="flex justify-between items-center">
              <CardTitle style={{ fontFamily: 'Young Serif, serif' }}>Pending Quotes</CardTitle>
              <Link to="/agent/quotes">
                <Button variant="ghost" size="sm">
                  View All <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </Link>
            </div>
          </CardHeader>
          <CardContent>
            {recentQuotes.length > 0 ? (
              <div className="space-y-3">
                {recentQuotes.map((quote) => (
                  <div 
                    key={quote.quote_id} 
                    className="flex items-center justify-between p-3 bg-muted/50 rounded-lg cursor-pointer hover:bg-muted"
                    onClick={() => navigate("/agent/quotes")}
                  >
                    <div>
                      <p className="font-medium text-sm">{quote.user?.full_name || quote.user?.email}</p>
                      <p className="text-xs text-muted-foreground">
                        ${quote.monthly_rent}/mo rent • ${quote.monthly_premium}/mo premium
                      </p>
                    </div>
                    <Badge className={getStatusBadge(quote.status)}>{quote.status}</Badge>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-muted-foreground py-8">No pending quotes</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Recent Activity */}
      {recentActivity.length > 0 && (
        <Card className="card-organic mt-6">
          <CardHeader>
            <CardTitle style={{ fontFamily: 'Young Serif, serif' }}>
              <Activity className="h-5 w-5 inline mr-2" />
              Recent Activity
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {recentActivity.map((activity, index) => (
                <div 
                  key={index}
                  className="flex items-center justify-between p-3 bg-muted/50 rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-full ${
                      activity.type === 'claim' ? 'bg-amber-100' :
                      activity.type === 'quote' ? 'bg-blue-100' :
                      activity.type === 'policy' ? 'bg-green-100' : 'bg-purple-100'
                    }`}>
                      {activity.type === 'claim' && <AlertCircle className="h-4 w-4 text-amber-600" />}
                      {activity.type === 'quote' && <FileText className="h-4 w-4 text-blue-600" />}
                      {activity.type === 'policy' && <NestIcon className="h-4 w-4 text-green-600" />}
                      {activity.type === 'payment' && <DollarSign className="h-4 w-4 text-purple-600" />}
                    </div>
                    <div>
                      <p className="font-medium text-sm capitalize">{activity.type}</p>
                      <p className="text-xs text-muted-foreground">
                        {activity.user?.full_name || activity.user?.email || 'Unknown'}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    {activity.amount && (
                      <p className="font-medium text-sm">${activity.amount}</p>
                    )}
                    <p className="text-xs text-muted-foreground">
                      {new Date(activity.timestamp).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </AgentLayout>
  );
}
